import type { Position } from "@pentacode/core";
import { LitElement, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/scroller";
import { app } from "../../../init";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/positions-form";
import type { PositionsForm } from "../components/positions-form";

@customElement("ptc-employees-positions-single")
export class EmployeesPositionsSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<id>\d+)\/positions/;

    @routeProperty({ arg: "id", type: Number })
    employeeId: number;

    get routeTitle() {
        return `Positionen: ${this._employee && this._employee.name}`;
    }

    async handleRoute() {
        if (!this.employeeId) {
            return;
        }

        this._employeePositions = structuredClone(this._employee?.positions || []);
    }

    private get _employee() {
        return app.getEmployee(this.employeeId);
    }

    @state()
    private _loading = false;

    @state()
    private _employeePositions: Position[] = [];

    @state()
    private _hasPositionsChanged = false;

    @query("ptc-positions-form")
    private _positionsForm: PositionsForm;

    private async _submitPositions(e: FocusEvent) {
        e.preventDefault();

        this._loading = true;

        try {
            await app.updateEmployee(this._employee!, {
                positions: this._positionsForm.positions.map((pos) => pos.id),
            });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
        this._checkPositionsChanged();
    }

    private _resetPositions() {
        this._employeePositions = structuredClone(this._employee?.positions) || [];
        this._hasPositionsChanged = false;
    }

    private _checkPositionsChanged() {
        const localPositions = this._positionsForm?.positions;

        if (!localPositions) {
            return false;
        }

        const hasDifferentLength = localPositions?.length !== (this._employee?.positions.length || 0);
        const hasDifferentPositionsOrder = localPositions.some((position, i) => {
            const employeePosition = this._employee?.positions[i];
            return employeePosition && position.id !== employeePosition.id;
        });

        const hasChanged = hasDifferentLength || hasDifferentPositionsOrder;
        this._hasPositionsChanged = hasChanged;
    }

    static styles = [shared];

    render() {
        const emp = this._employee;
        if (!emp) {
            return;
        }

        return html`
            <div class="fullbleed positions">
                <div class="vertical layout fit-vertically">
                    <ptc-scroller class="stretch">
                        <div class="double-padded narrow-content">
                            <h2>Positionen / Einsatzgebiete</h2>
                            <ptc-positions-form
                                .positions=${this._employeePositions || []}
                                @change=${this._checkPositionsChanged}
                            ></ptc-positions-form>
                        </div>
                    </ptc-scroller>

                    <div
                        class="horizontal centering padded layout narrow-content w-full"
                        ?hidden=${!this._hasPositionsChanged}
                        ?disabled=${this._loading}
                    >
                        <div class="horizontal spacing evenly stretching layout stretch">
                            <button class="primary" @click=${this._submitPositions}>Speichern</button>
                            <button class="transparent" type="button" @click=${this._resetPositions}>Abbrechen</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
